import React from 'react'
import { Copy, ExternalLink, Link2 } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'

interface ProfileActionLinksProps {
  /** When false, hides the copy-id button (e.g. person enhedsnummer). Default true. */
  showCopyId?: boolean
  /** Value copied by the first (copy) button — e.g. CVR. */
  copyValue?: string
  copyAriaLabel?: string
  copyTitle?: string
  copySuccessMessage?: string
  shareUrl: string
  datacvrHref: string
  datacvrAriaLabel: string
}

/**
 * Copy-id, copy-page-link, and open-on-DataCVR actions shared by company
 * and person profile headers.
 */
const ProfileActionLinks: React.FC<ProfileActionLinksProps> = ({
  showCopyId = true,
  copyValue = '',
  copyAriaLabel = '',
  copyTitle = '',
  copySuccessMessage = '',
  shareUrl,
  datacvrHref,
  datacvrAriaLabel,
}) => {
  const { showToast } = useToast()

  const copyText = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showToast(successMessage)
    } catch {
      showToast('Kunne ikke kopiere til udklipsholder.', { tone: 'signal' })
    }
  }

  return (
    <span
      className={`inline-flex items-center gap-0.5 ${
        showCopyId ? 'border-l border-rule pl-2 ml-0.5' : ''
      }`}
      role="group"
      aria-label="Kopiér og del"
    >
      {showCopyId && (
        <button
          type="button"
          onClick={() => copyText(copyValue, copySuccessMessage)}
          className="inline-flex items-center justify-center rounded-sm p-1 text-ink-500 hover:bg-paper-200 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal"
          aria-label={copyAriaLabel}
          title={copyTitle}
        >
          <Copy className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      <button
        type="button"
        onClick={() => copyText(shareUrl, 'Link kopieret.')}
        className="inline-flex items-center justify-center rounded-sm p-1 text-ink-500 hover:bg-paper-200 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal"
        aria-label="Kopiér link til denne side"
        title="Kopiér link til denne side"
      >
        <Link2 className="h-3.5 w-3.5" aria-hidden />
      </button>
      <a
        href={datacvrHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-sm p-1 text-ink-500 hover:bg-paper-200 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal"
        aria-label={datacvrAriaLabel}
        title="Åbn på DataCVR (datacvr.virk.dk)"
      >
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      </a>
    </span>
  )
}

export default ProfileActionLinks
